import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router";
import { AxiosError } from "axios";

import {
  OtpInput,
  PennButton,
  PennDropdown,
  PennField,
  PennInput,
  PennLink,
  PennText,
  PennTextarea,
} from "@/components/ui";

import { abandonRegistration } from "@penn/web-common";
import { registerSchema, type RegisterFormData } from "../../schemas/register";
import { useDepartments } from "../../hooks/useDepartments";
import { useRegions } from "../../hooks/useRegions";
import { useRegister } from "../../hooks/useRegister";
import {
  cognitoSignOut,
  cognitoSignUp,
  completeAutoSignIn,
  confirmSignUpCode,
  newThrowawayPassword,
  resendSignUpCode,
  type ConfirmSignUpResult,
} from "@penn/web-common";
import { API_BASE_URL } from "@penn/web-common";
import { completePennSession } from "@penn/web-common";
import { useAppDispatch } from "../../stores/hooks";
import {
  formatRegistrationCodeTime,
  registrationCodeDeadlineMs,
  registrationCodeSecondsRemaining,
  REGISTRATION_CODE_WINDOW_MINUTES,
  REGISTRATION_CODE_WINDOW_SECONDS,
} from "../../utils/registration-code-window";

// Sign-up verification code length. Cognito's default confirmation code is 6
// digits. The pre-sign-up Lambda leaves the web client unconfirmed
// (autoConfirmUser=false), so a CONFIRM_SIGN_UP code is always emailed and this
// proof step is always reached before either auto-sign-in or the Login fallback.
const CONFIRM_CODE_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;
const REGISTRATION_TIMEOUT_MESSAGE =
  `Your registration session timed out after ${REGISTRATION_CODE_WINDOW_MINUTES} minutes. ` +
  "Click Resend code to receive a new code.";

interface ApiError {
  error?: { code?: string; message?: string };
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as ApiError | undefined;
    const code = data?.error?.code;
    if (code === "duplicate-email") {
      return "An account with this email already exists. Please sign in instead.";
    }
    if (code === "cognito-sub-already-bound") {
      return "This Cognito identity is already bound to another account.";
    }
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the API gateway. Is svc-01-api-gateway running on :4001 (and svc-03 on :4003 behind it)?";
    }
    return data?.error?.message ?? "Registration failed. Please try again.";
  }
  if (err instanceof Error) {
    const m = err.message ?? "";
    if (/UsernameExistsException/i.test(m) || /User already exists/i.test(m)) {
      return "An account with this email already exists. Please sign in instead.";
    }
    if (/InvalidParameterException/i.test(m) || /not allowed/i.test(m)) {
      return "Sign-up rejected — your email domain is not on the allowlist for this app.";
    }
    return m || "Registration failed. Please try again.";
  }
  return "Registration failed. Please try again.";
}

type Step = "form" | "confirm";

interface RegisterLocationState {
  email?: string;
}

export const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  // Issue 4: LoginPage redirects unregistered users here with their email.
  const prefillEmail =
    (location.state as RegisterLocationState | null)?.email ?? "";

  const [step, setStep] = useState<Step>("form");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string>("");

  const [otp, setOtp] = useState("");
  const [otpWorking, setOtpWorking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [registrationDeadlineMs, setRegistrationDeadlineMs] = useState<
    number | null
  >(null);
  const [registrationSecondsRemaining, setRegistrationSecondsRemaining] =
    useState(REGISTRATION_CODE_WINDOW_SECONDS);
  const otpAutoSubmittedRef = useRef(false);

  // Issue 8 (OTP-mandatory): the local `users` row is written only in
  // `finishRegistration` (right before authentication completion), so an abandoned
  // sign-up that never reaches that point leaves only an unverified Cognito
  // identity. `pendingRegRef` holds the data needed to (a) finish the register
  // and (b) clean up the orphan if the user leaves the confirm step.
  // `completedRef` flips true once the DB row exists (or after a hard failure
  // we explicitly own) so we never delete a real account.
  const pendingRegRef = useRef<{
    data: RegisterFormData;
    cognitoSub: string;
  } | null>(null);
  const completedRef = useRef(false);

  const departments = useDepartments();
  const regions = useRegions();
  const register$ = useRegister();

  const departmentOptions = useMemo(
    () =>
      (departments?.data ?? []).map((d) => ({
        value: String(d.id),
        label: d.displayName,
      })),
    [departments?.data],
  );

  const regionOptions = useMemo(
    () =>
      (regions.data ?? []).map((r) => ({
        value: r.code,
        label: r.displayName,
      })),
    [regions.data],
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      regionCode: "NA" as const,
      email: prefillEmail,
      firstName: "John",
      lastName: "Doe",
      telephone: "+1 (555) 123-4567",
      company: "Acme Corp",
      companyAddress: "123 Main Street, Springfield, IL 62701",
      departmentId: 1,
      // Test emails to try (domain classification will be logged):
      // Internal: user@pemnet.com, admin@profil.eu → prints "internal (domain: ...)"
      // External: user@gmail.com, customer@example.com → prints "external (domain: ...)"
    },
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (step !== "confirm" || registrationDeadlineMs == null) return;
    const updateRemaining = () => {
      setRegistrationSecondsRemaining(
        registrationCodeSecondsRemaining(registrationDeadlineMs),
      );
    };
    updateRemaining();
    const id = window.setInterval(updateRemaining, 1_000);
    return () => window.clearInterval(id);
  }, [registrationDeadlineMs, step]);

  const registrationWindowExpired =
    step === "confirm" &&
    registrationDeadlineMs != null &&
    registrationSecondsRemaining <= 0;

  useEffect(() => {
    if (!registrationWindowExpired) return;
    setOtp("");
    setResendNotice(null);
    otpAutoSubmittedRef.current = false;
    setSubmitError(REGISTRATION_TIMEOUT_MESSAGE);
  }, [registrationWindowExpired]);

  const startRegistrationWindow = () => {
    setRegistrationDeadlineMs(registrationCodeDeadlineMs());
    setRegistrationSecondsRemaining(REGISTRATION_CODE_WINDOW_SECONDS);
  };

  // Issue 8: clean up an unverified Cognito identity when the user abandons
  // the confirm step. Two triggers:
  //   1. Hard close / refresh → `pagehide` + navigator.sendBeacon (the only
  //      transport that survives an unloading document).
  //   2. SPA route-away (e.g. clicking "Sign in") → unmount cleanup → the
  //      regular axios call.
  // Both are guarded server-side (svc-03 never deletes an active account), and
  // skipped once `completedRef` is set (the DB row exists → real account).
  useEffect(() => {
    const onPageHide = () => {
      const pending = pendingRegRef.current;
      if (!pending || completedRef.current) return;
      try {
        const blob = new Blob([JSON.stringify({ email: pending.data.email })], {
          type: "application/json",
        });
        navigator.sendBeacon(
          `${API_BASE_URL}/v1/users/abandon-registration`,
          blob,
        );
      } catch {
        /* best-effort — the periodic sweep is the backstop */
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      const pending = pendingRegRef.current;
      if (pending && !completedRef.current) {
        void abandonRegistration(pending.data.email);
        void cognitoSignOut();
      }
    };
  }, []);

  const finishRegistration = async (confirmResult: ConfirmSignUpResult) => {
    // Create (or reactivate) the local `users` row bound to the Cognito
    // identity before exchanging any Cognito session. svc-02 intentionally
    // rejects an identity that does not yet have this local row.
    const pending = pendingRegRef.current;
    if (!pending) {
      throw new Error("Your registration session expired. Please start over.");
    }
    await register$.mutateAsync({
      email: pending.data.email,
      firstName: pending.data.firstName,
      lastName: pending.data.lastName,
      regionCode: pending.data.regionCode,
      departmentId: pending.data.departmentId,
      phone: pending.data.telephone,
      companyName: pending.data.company,
      companyAddress: pending.data.companyAddress,
      cognitoSub: pending.cognitoSub,
      appContext: "web",
    });
    // DB row now exists → this is a real account; never abandon-delete it.
    completedRef.current = true;
    pendingRegRef.current = null;

    // Amplify can continue the just-confirmed USER_AUTH sign-up in the same tab
    // without sending a second OTP. Its continuation is short-lived, so every
    // failure deliberately falls back to the existing prefilled Login flow.
    if (confirmResult.continuation === "complete_auto_sign_in") {
      try {
        const auto = await completeAutoSignIn();
        if (auto.signedIn) {
          await completePennSession({
            email: pending.data.email,
            dispatch,
            navigate,
          });
          return;
        }
      } catch {
        // Fall through to the current manual sign-in path.
      }
    }

    await cognitoSignOut();
    navigate("/login", {
      replace: true,
      state: { email: pending.data.email, registered: true },
    });
  };

  const onSubmit = async (data: RegisterFormData) => {
    setSubmitError(null);

    try {
      // Step 1: create the Cognito user. The throwaway password is required by
      // Amplify's signUp() API but unused (sign-in is passwordless EMAIL_OTP).
      const cognitoOut = await cognitoSignUp({
        email: data.email,
        password: newThrowawayPassword(),
        userAttributes: {
          given_name: data.firstName,
          family_name: data.lastName,
        },
      });

      // Stash the form + cognitoSub. `completedRef` flips true once the DB row
      // exists so the abandon-on-leave hook never deletes a real account.
      pendingRegRef.current = { data, cognitoSub: cognitoOut.cognitoSub };
      completedRef.current = false;
      setRegisteredEmail(data.email);

      // The pre-sign-up Lambda does not auto-confirm the web client, so Cognito
      // issues a CONFIRM_SIGN_UP step and emails the signup-branded code.
      // Amplify may continue into auto-sign-in after this required proof.
      setOtp("");
      otpAutoSubmittedRef.current = false;
      setStep("confirm");
      setResendCooldown(RESEND_COOLDOWN_S);
      startRegistrationWindow();
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  const confirmEmail = async (code: string) => {
    if (otpWorking) return;
    if (
      registrationDeadlineMs == null ||
      Date.now() >= registrationDeadlineMs
    ) {
      setRegistrationSecondsRemaining(0);
      otpAutoSubmittedRef.current = false;
      setSubmitError(REGISTRATION_TIMEOUT_MESSAGE);
      return;
    }
    if (code.length !== CONFIRM_CODE_LENGTH || !/^\d+$/.test(code)) {
      setSubmitError(`Enter the ${CONFIRM_CODE_LENGTH}-digit code.`);
      return;
    }
    setSubmitError(null);
    setOtpWorking(true);
    try {
      const confirmResult = await confirmSignUpCode(registeredEmail, code);
      await finishRegistration(confirmResult);
    } catch (err) {
      otpAutoSubmittedRef.current = false;
      setSubmitError(extractErrorMessage(err));
    } finally {
      setOtpWorking(false);
    }
  };

  const handleOtpChange = (next: string) => {
    setOtp(next);
    if (submitError) setSubmitError(null);
  };

  const handleOtpComplete = (code: string) => {
    if (otpAutoSubmittedRef.current) return;
    otpAutoSubmittedRef.current = true;
    void confirmEmail(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || otpWorking || !registeredEmail) return;
    setResendNotice(null);
    setSubmitError(null);
    setOtpWorking(true);
    try {
      await resendSignUpCode(registeredEmail);
      setOtp("");
      otpAutoSubmittedRef.current = false;
      setResendCooldown(RESEND_COOLDOWN_S);
      startRegistrationWindow();
      setSubmitError(null);
      setResendNotice(`A new code was sent to ${registeredEmail}.`);
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setOtpWorking(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (otpWorking || !registeredEmail) return;
    setOtpWorking(true);
    setSubmitError(null);
    try {
      // Explicit abandon — mark handled so unmount cleanup won't double-delete.
      completedRef.current = true;
      pendingRegRef.current = null;
      await abandonRegistration(registeredEmail).catch(() => undefined);
      await cognitoSignOut();
      navigate("/login", { replace: true });
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setOtpWorking(false);
    }
  };

  // The form-submit phase does Cognito signUp, which advances to the required
  // confirmation step below. react-hook-form's `isSubmitting` covers the async
  // window.
  const isLookupsLoading = departments.isLoading || regions.isLoading;
  const lookupsError = departments.error || regions.error;

  if (step === "confirm") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-soft px-8 py-10">
            <div className="text-center mb-8">
              <PennText as="h1" size="2xl" color="blue_grey1" font="bold">
                Verify your email
              </PennText>
              <PennText as="p" size="sm" color="blue_grey1" className="mt-1">
                We sent a {CONFIRM_CODE_LENGTH}-digit verification code to{" "}
                {registeredEmail}.
              </PennText>
              <PennText as="p" size="sm" color="blue_grey" className="mt-1">
                Didn't get it? Please check your spam or junk folder.
              </PennText>
              <PennText
                as="p"
                size="sm"
                color={registrationWindowExpired ? "error" : "blue_grey"}
                className="mt-2"
              >
                {registrationWindowExpired
                  ? "Registration timed out. Click Resend code below to continue."
                  : `Complete registration within ${formatRegistrationCodeTime(registrationSecondsRemaining)}. No new code is sent automatically.`}
              </PennText>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void confirmEmail(otp);
              }}
              noValidate
              className="space-y-5"
            >
              <PennField label="Verification code" htmlFor="otp" required>
                <OtpInput
                  length={CONFIRM_CODE_LENGTH}
                  value={otp}
                  onChange={handleOtpChange}
                  onComplete={handleOtpComplete}
                  autoFocus
                  error={!!submitError}
                  disabled={otpWorking || registrationWindowExpired}
                />
              </PennField>

              {resendNotice && (
                <div
                  role="status"
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                >
                  {resendNotice}
                </div>
              )}

              {submitError && (
                <div
                  role="alert"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {submitError}
                </div>
              )}

              <PennButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={
                  otpWorking ||
                  registrationWindowExpired ||
                  otp.length !== CONFIRM_CODE_LENGTH
                }
                className="mt-2 bg-penn-500"
              >
                {otpWorking ? "Verifying…" : "Verify and continue"}
              </PennButton>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={otpWorking || resendCooldown > 0}
                  className="text-penn-600 hover:text-penn-700 disabled:text-gray-400 font-medium"
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : "Resend code"}
                </button>
              </div>

              <PennButton
                type="button"
                variant="secondary"
                size="md"
                fullWidth
                onClick={() => void handleCancelRegistration()}
                disabled={otpWorking}
              >
                Cancel registration
              </PennButton>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center px-4 pb-12 pt-[152px] overflow-y-auto">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-soft px-8 py-10">
          <div className="text-center mb-8">
            <PennText as="h1" size="2xl" color="blue_grey1" font="bold">
              Create an account
            </PennText>
            <PennText as="p" size="sm" color="blue_grey1" className="mt-1">
              Fill in the details below to get started
            </PennText>
          </div>

          {lookupsError && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              Could not load departments/regions. The user service may not be
              running. You can still try to submit — required dropdowns will
              fall back to manual entry once the service is reachable.
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <PennField
                label="First name"
                required
                htmlFor="firstName"
                error={errors.firstName?.message}
              >
                <PennInput
                  id="firstName"
                  type="text"
                  placeholder="First name"
                  maxLength={30}
                  error={!!errors.firstName}
                  {...register("firstName")}
                />
              </PennField>
              <PennField
                label="Last name"
                required
                htmlFor="lastName"
                error={errors.lastName?.message}
              >
                <PennInput
                  id="lastName"
                  type="text"
                  placeholder="Last name"
                  maxLength={30}
                  error={!!errors.lastName}
                  {...register("lastName")}
                />
              </PennField>
            </div>

            <PennField
              label="Email"
              required
              htmlFor="email"
              error={errors.email?.message}
            >
              <PennInput
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                maxLength={100}
                error={!!errors.email}
                {...register("email")}
              />
            </PennField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <PennField
                label="Company"
                required
                htmlFor="company"
                error={errors.company?.message}
              >
                <PennInput
                  id="company"
                  type="text"
                  placeholder="Company name"
                  maxLength={100}
                  error={!!errors.company}
                  {...register("company")}
                />
              </PennField>
              <PennField
                label="Telephone number"
                required
                htmlFor="telephone"
                error={errors.telephone?.message}
              >
                <PennInput
                  id="telephone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  error={!!errors.telephone}
                  {...register("telephone")}
                />
              </PennField>
            </div>

            <PennField
              label="Company address"
              required
              htmlFor="companyAddress"
              error={errors.companyAddress?.message}
            >
              <PennTextarea
                id="companyAddress"
                rows={3}
                placeholder="Street, ZIP, City, Country"
                error={!!errors.companyAddress}
                {...register("companyAddress")}
              />
            </PennField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <PennField
                label="Function / Department"
                required
                htmlFor="departmentId"
                error={errors.departmentId?.message}
              >
                <Controller
                  name="departmentId"
                  control={control}
                  render={({ field }) => (
                    <PennDropdown
                      inputId="departmentId"
                      name={field.name}
                      options={departmentOptions}
                      value={field.value != null ? String(field.value) : ""}
                      onChange={(value) => {
                        const next =
                          typeof value === "string" && value.length > 0
                            ? Number.parseInt(value, 10)
                            : Number.NaN;
                        field.onChange(
                          Number.isFinite(next) ? next : undefined,
                        );
                      }}
                      onBlur={field.onBlur}
                      placeholder={
                        isLookupsLoading
                          ? "Loading departments…"
                          : departmentOptions.length === 0
                            ? "No departments available"
                            : "Select a function…"
                      }
                      isDisabled={isLookupsLoading}
                      error={!!errors.departmentId}
                    />
                  )}
                />
              </PennField>
              <PennField
                label="Region"
                required
                htmlFor="regionCode"
                error={errors.regionCode?.message}
              >
                <Controller
                  name="regionCode"
                  control={control}
                  render={({ field }) => (
                    <PennDropdown
                      inputId="regionCode"
                      name={field.name}
                      options={regionOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={
                        isLookupsLoading
                          ? "Loading regions…"
                          : "Select a region"
                      }
                      isDisabled={isLookupsLoading}
                      error={!!errors.regionCode}
                    />
                  )}
                />
              </PennField>
            </div>

            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {submitError}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <PennButton
                type="button"
                variant="secondary"
                size="md"
                onClick={() => navigate("/login")}
                disabled={isSubmitting}
              >
                Cancel
              </PennButton>
              <PennButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={isSubmitting}
                className="bg-penn-500"
              >
                {isSubmitting ? "Creating…" : "Create account"}
              </PennButton>
            </div>
          </form>

          <PennText
            as="p"
            size="sm"
            align="center"
            className="mt-6 text-gray-500"
          >
            Already have an account?{" "}
            <PennLink to="/login" size="sm" font="medium">
              Sign in
            </PennLink>
          </PennText>
        </div>
      </div>
    </div>
  );
};
